import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { type AudioDeviceEvent, getAudioDeviceEventLog } from "@/utils/audio-device-event-log.utils";

export type IncidentAudioDevice = {
    deviceId: string;
    label: string;
    kind: MediaDeviceKind;
    groupId: string;
};

export type IncidentAudioDevicesSnapshot = {
    selectedInputDeviceId: string | null;
    selectedOutputDeviceId: string | null;
    devices: IncidentAudioDevice[];
    error?: string;
};

export function collectLocalStorage(): Record<string, string> {
    const out: Record<string, string> = {};
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;
            out[key] = localStorage.getItem(key) ?? "";
        }
    } catch {
        out["_error"] = "Could not read localStorage";
    }
    return out;
}

// Enumerate connected audio devices so incident reports include hardware context.
// Labels are only populated when the user has already granted microphone permission;
// otherwise the browser returns empty strings for privacy reasons.
export async function collectAudioDevices(): Promise<IncidentAudioDevicesSnapshot> {
    const readSelected = (key: string): string | null => {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            // Values are JSON-encoded by useLocalStorage.
            try {
                const parsed = JSON.parse(raw);
                return typeof parsed === "string" ? parsed : null;
            } catch {
                return raw;
            }
        } catch {
            return null;
        }
    };

    const selectedInputDeviceId = readSelected(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key);
    const selectedOutputDeviceId = readSelected(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key);

    try {
        if (!navigator.mediaDevices?.enumerateDevices) {
            return {
                selectedInputDeviceId,
                selectedOutputDeviceId,
                devices: [],
                error: "mediaDevices.enumerateDevices not available",
            };
        }

        const all = await navigator.mediaDevices.enumerateDevices();
        const devices: IncidentAudioDevice[] = all
            .filter((device) => device.kind === "audioinput" || device.kind === "audiooutput")
            .map((device) => ({
                deviceId: device.deviceId,
                label: device.label,
                kind: device.kind,
                groupId: device.groupId,
            }));

        return { selectedInputDeviceId, selectedOutputDeviceId, devices };
    } catch (error) {
        return {
            selectedInputDeviceId,
            selectedOutputDeviceId,
            devices: [],
            error: error instanceof Error ? error.message : "Could not enumerate audio devices",
        };
    }
}

// Returns the rolling history of audio device events (connect, disconnect, auto-switch, selection)
// so reviewers can see what happened to the user's hardware leading up to the incident.
export function collectAudioDeviceEventLog(): AudioDeviceEvent[] {
    try {
        return getAudioDeviceEventLog();
    } catch {
        return [];
    }
}
